import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

/**
 * Выдвижная панель под палец: слева — навигация, снизу — меню и формы.
 *
 * Библиотека (vaul / shadcn Drawer) намеренно НЕ берётся — по той же причине, по
 * которой в редакторе строки нет ProseMirror: она приносит собственный визуальный
 * язык и собственный слой доступности, а нужны от неё три вещи — затемнение,
 * въезд и утаскивание пальцем. Всё три занимают меньше, чем стоило бы согласование
 * её токенов с нашими.
 *
 * Панель едет НЕ поверх всего экрана, а из того края, к которому относится:
 * навигация живёт слева и на десктопе, меню строки — там же, где панель действий.
 * Направление появления — это подсказка, откуда взялось и куда вернётся.
 */
export function Drawer({
	open,
	side = "bottom",
	title,
	onClose,
	children
}: {
	open: boolean;
	side?: "left" | "bottom";
	/** Заголовок. У нижней панели заодно служит зоной перетаскивания. */
	title?: string;
	onClose: () => void;
	children: ReactNode;
}) {
	const [mounted, setMounted] = useState(open);
	const [shown, setShown] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (open) {
			setMounted(true);
			// Кадр между монтированием и «показать» — иначе браузеру нечего анимировать:
			// он увидит сразу конечное состояние.
			const frame = requestAnimationFrame(() => setShown(true));
			return () => cancelAnimationFrame(frame);
		}
		setShown(false);
		const timer = setTimeout(() => setMounted(false), 220);
		return () => clearTimeout(timer);
	}, [open]);

	useEffect(() => {
		if (!open) {
			return;
		}
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				e.stopPropagation();
				onClose();
			}
		};
		document.addEventListener("keydown", handler, true);
		return () => document.removeEventListener("keydown", handler, true);
	}, [open, onClose]);

	const drag = useDismissDrag(side, onClose, panelRef);

	if (!mounted) {
		return null;
	}

	return (
		<div className="drawer-root" data-open={shown} data-side={side}>
			{/* `touch-action: none` на затемнении: без него палец, промахнувшийся мимо
			    панели, прокручивает список ПОД ней. */}
			<button type="button" className="drawer-scrim" aria-label="Закрыть" onClick={onClose} />
			<div ref={panelRef} className="drawer-panel" role="dialog" aria-modal="true" aria-label={title}>
				{side === "bottom" ? (
					<div className="drawer-grip" {...drag}>
						<span className="drawer-grip-bar" />
						{title && <span className="section-label">{title}</span>}
					</div>
				) : null}
				<div className="drawer-body" {...(side === "left" ? drag : {})}>
					{children}
				</div>
			</div>
		</div>
	);
}

/** Сдвиг, после которого жест считается утаскиванием, а не дрожанием пальца. */
const ENGAGE = 12;

/**
 * Утаскивание пальцем. Смысл не в анимации: панель, которую можно только закрыть
 * кнопкой, на телефоне ощущается как модалка, а она — не модалка.
 *
 * Порог — четверть размера панели: меньше собирает случайные закрытия при скролле,
 * больше требует полного протаскивания.
 */
function useDismissDrag(
	side: "left" | "bottom",
	onClose: () => void,
	panelRef: React.RefObject<HTMLDivElement | null>
) {
	const start = useRef<{ x: number; y: number } | null>(null);
	const shift = useRef(0);
	/* Пока жест не признан «утаскиванием», панель не двигается: иначе обычная прокрутка
	   списка внутри шторки дёргала бы её вбок на каждый пиксель дрожания пальца. */
	const engaged = useRef(false);

	const move = useCallback(
		(offset: number) => {
			const panel = panelRef.current;
			if (panel) {
				panel.style.transform = side === "bottom" ? `translateY(${offset}px)` : `translateX(${-offset}px)`;
				panel.style.transition = "none";
			}
		},
		[side, panelRef]
	);

	const release = useCallback(() => {
		const panel = panelRef.current;
		if (panel) {
			panel.style.transform = "";
			panel.style.transition = "";
		}
	}, [panelRef]);

	return {
		onPointerDown: (e: React.PointerEvent) => {
			if (e.pointerType === "mouse") {
				return; // мышью панель таскать незачем — есть затемнение и Esc
			}
			start.current = { x: e.clientX, y: e.clientY };
			shift.current = 0;
			engaged.current = false;
		},
		onPointerMove: (e: React.PointerEvent) => {
			if (!start.current) {
				return;
			}
			const dx = e.clientX - start.current.x;
			const dy = e.clientY - start.current.y;
			const along = side === "bottom" ? dy : -dx;
			const across = side === "bottom" ? Math.abs(dx) : Math.abs(dy);
			if (!engaged.current) {
				if (along < ENGAGE || along <= across) {
					return;
				}
				engaged.current = true;
			}
			shift.current = Math.max(0, along);
			move(shift.current);
		},
		onPointerUp: () => {
			const dragged = engaged.current;
			start.current = null;
			engaged.current = false;
			if (!dragged) {
				return;
			}
			const panel = panelRef.current;
			const size = side === "bottom" ? (panel?.offsetHeight ?? 1) : (panel?.offsetWidth ?? 1);
			release();
			if (shift.current > size / 4) {
				onClose();
			}
		},
		onPointerCancel: () => {
			start.current = null;
			engaged.current = false;
			release();
		}
	};
}
